import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getDataWAuth } from "../../../utils/api-service";
import { DASHBOARD_API } from "../../../constants/dashboardConstants";

export const fetchCcmDashboardData = createAsyncThunk(
  "ccmDashboard/fetchCcmDashboardData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getDataWAuth(DASHBOARD_API.CCM_DASHBOARD);
      const backendData = response.data;

      return {
        extensionRequests: (backendData.extensionRequests || []).map((item, index) => ({
          id: index + 1,
          country: item.country,
          bp: item.bp ?? 0,
          ccm: item.ccm ?? 0,
        })),
        pendingErrors: (backendData.pendingErrors || []).map((item, index) => ({
          id: index + 1,
          country: item.country,
          errors: item.errors ?? 0,
        })),
        pendingBPCheck: (backendData.pendingBPCheck || []).map((item, index) => ({
          id: index + 1,
          country: item.country,
          pending: item.pending ?? 0,
        })),
        newBusiness: backendData.newBusiness || null,
        rateStatus: backendData.rateStatus || null,
        bookedInterest: backendData.bookedInterest || null,
        autoApprovalRate: backendData.autoApprovalRate || null,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch CCM dashboard data");
    }
  }
);

export const fetchCcmAutoApprovalRate = createAsyncThunk(
  "ccmDashboard/fetchCcmAutoApprovalRate",
  async ({ country, year, month }, { rejectWithValue }) => {
    try {
      const params = {};
      if (country) params.country = country;
      if (year) params.year = year;
      if (month) params.month = month;

      const queryString = new URLSearchParams(params).toString();
      const url = `${DASHBOARD_API.CCM_AUTO_APPROVAL_RATE}?${queryString}`;

      const response = await getDataWAuth(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch CCM auto approval rate");
    }
  }
);

export const fetchAvailableYearsForCcmApprovalRate = createAsyncThunk(
  "ccmDashboard/fetchAvailableYearsForCcmApprovalRate",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getDataWAuth(`${DASHBOARD_API.CCM_AUTO_APPROVAL_RATE}/availableYears`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch available years");
    }
  }
);

const initialState = {
  extensionRequests: [],
  pendingErrors: [],
  pendingBPCheck: [],
  newBusiness: null,
  rateStatus: null,
  bookedInterest: null,
  autoApprovalRate: null,
  availableYears: [],
  yearsWithMonths: {},
  autoApprovalRateLoading: false,
  loading: false,
  error: null,
};

const ccmDashboardSlice = createSlice({
  name: "ccmDashboard",
  initialState,
  reducers: {
    clearCCMDashboardError: (state) => {
      state.error = null;
    },

    resetCCMDashboard: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCcmDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCcmDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.extensionRequests = action.payload.extensionRequests;
        state.pendingErrors = action.payload.pendingErrors;
        state.pendingBPCheck = action.payload.pendingBPCheck;
        state.newBusiness = action.payload.newBusiness;
        state.rateStatus = action.payload.rateStatus;
        state.bookedInterest = action.payload.bookedInterest;
        state.autoApprovalRate = action.payload.autoApprovalRate;
      })
      .addCase(fetchCcmDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCcmAutoApprovalRate.pending, (state) => {
        state.autoApprovalRateLoading = true;
        state.error = null;
      })
      .addCase(fetchCcmAutoApprovalRate.fulfilled, (state, action) => {
        state.autoApprovalRateLoading = false;
        state.autoApprovalRate = action.payload;
      })
      .addCase(fetchCcmAutoApprovalRate.rejected, (state, action) => {
        state.autoApprovalRateLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAvailableYearsForCcmApprovalRate.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchAvailableYearsForCcmApprovalRate.fulfilled, (state, action) => {
        if (action.payload && typeof action.payload === 'object') {
          state.yearsWithMonths = action.payload;
          state.availableYears = Object.keys(action.payload).sort((a, b) => b.localeCompare(a));
        } else {
          state.yearsWithMonths = {};
          state.availableYears = [];
        }
      })
      .addCase(fetchAvailableYearsForCcmApprovalRate.rejected, (state, action) => {
        state.error = action.payload;
        state.yearsWithMonths = {};
        state.availableYears = [];
      });
  },
});

export const {
  clearCCMDashboardError,
  resetCCMDashboard,
} = ccmDashboardSlice.actions;

export default ccmDashboardSlice.reducer;
